/**
 * Matricule national luxembourgeois (CNS), 13 chiffres : `AAAAMMJJXXXC1C2`.
 * - `AAAAMMJJ` date de naissance ;
 * - `XXX` rang de naissance ;
 * - `C1` clé Luhn 10 des 11 premiers chiffres ;
 * - `C2` clé Verhoeff des 11 premiers chiffres.
 * Algorithme porté à l'identique de la V1 (`client/src/lib/ssn-validation.ts`).
 * Moteur du pays `LU` de `ssn.ts`, qui y ajoute le NIR français, le NISS belge et « autre ». La
 * validation elle-même — date, bornes, clés, codes d'erreur — y vit, une seule fois pour les
 * quatre pays : ce fichier ne garde que les clés, la normalisation et l'affichage.
 */

const CNS_LENGTH = 13;

function digitAt(value: string, index: number): number {
  const char = value.charAt(index);
  return char >= '0' && char <= '9' ? char.charCodeAt(0) - 48 : Number.NaN;
}

/** Luhn 10 : poids 2 sur les rangs pairs en partant de la droite de la base. */
function luhnCheckDigit(base: string): number {
  let checkSum = 0;
  for (let pos = 0; pos < base.length; pos++) {
    const weighted = digitAt(base, base.length - pos - 1) * (pos % 2 === 0 ? 2 : 1);
    checkSum += weighted > 9 ? weighted - 9 : weighted;
  }
  return (10 - (checkSum % 10)) % 10;
}

const VERHOEFF_INV = [0, 4, 3, 2, 1, 5, 6, 7, 8, 9] as const;

const VERHOEFF_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
] as const;

const VERHOEFF_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 2, 5, 7, 3],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
] as const;

function verhoeffP(position: number, digit: number): number {
  const row = VERHOEFF_P[position % 8];
  return row === undefined ? 0 : (row[digit] ?? 0);
}

function verhoeffD(left: number, right: number): number {
  const row = VERHOEFF_D[left];
  return row === undefined ? 0 : (row[right] ?? 0);
}

function verhoeffCheckDigit(base: string): number {
  let checkSum = 0;
  for (let pos = 0; pos < base.length; pos++) {
    const digit = digitAt(base, base.length - pos - 1);
    checkSum = verhoeffD(checkSum, verhoeffP(pos + 1, digit));
  }
  return VERHOEFF_INV[checkSum] ?? 0;
}

/** Retire espaces, points et tirets. Le matricule est saisi avec des séparateurs variés. */
export function normalizeCnsId(value: string): string {
  return value.replace(/[\s.-]/g, '');
}

/** Les deux clés d'un matricule à partir de ses 11 premiers chiffres. Sert aussi à fabriquer des jeux d'essai. */
export function cnsCheckDigits(base: string): { luhn: number; verhoeff: number } | null {
  if (!/^\d{11}$/.test(base)) return null;
  return { luhn: luhnCheckDigit(base), verhoeff: verhoeffCheckDigit(base) };
}

/** Matricule complet à partir de ses 11 premiers chiffres, clés calculées. `null` si la base est invalide. */
export function buildCnsId(base: string): string | null {
  const keys = cnsCheckDigits(base);
  return keys === null ? null : `${base}${String(keys.luhn)}${String(keys.verhoeff)}`;
}

/** Affichage groupé : `1893 01 20 123 45`. */
export function formatCnsId(value: string): string {
  const clean = normalizeCnsId(value);
  if (clean.length !== CNS_LENGTH) return value;
  return `${clean.slice(0, 4)} ${clean.slice(4, 6)} ${clean.slice(6, 8)} ${clean.slice(8, 11)} ${clean.slice(11)}`;
}
